/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.model.validation

object PhoneValidation {
    // Argentine mobile: +549 + area code (2-4 digits) + number (6-8 digits) = 13 digits total
    private val AR_MOBILE_REGEX = Regex("""^\+549\d{10}$""")

    fun isValidArgentinePhone(phone: String): Boolean =
        AR_MOBILE_REGEX.matches(phone)

    fun normalizePhone(input: String): String {
        val digits = input.filter { it.isDigit() }
        return when {
            digits.startsWith("549") && digits.length == 13 -> "+$digits"
            digits.startsWith("9") && digits.length == 11 -> "+54$digits"
            digits.startsWith("0") -> {
                // Strip leading 0, then strip mobile prefix 15 wherever it appears
                // e.g., "01115..." -> drop 0 -> "1115..." -> area=11, drop 15 -> "5..."
                val withoutZero = digits.drop(1)
                // Find and remove the "15" mobile prefix after the area code
                // Area codes are 2-4 digits, mobile prefix 15 follows
                val cleaned = withoutZero.replace(Regex("^(\\d{2,4})15"), "$1")
                "+549$cleaned"
            }
            digits.length == 10 -> "+549$digits"
            else -> "+$digits"
        }
    }

    fun toWhatsAppLink(phone: String, message: String): String {
        val normalized = normalizePhone(phone).removePrefix("+")
        val encoded = message.encodeForUrl()
        return "https://wa.me/$normalized?text=$encoded"
    }
}

internal fun String.encodeForUrl(): String = buildString {
    for (char in this@encodeForUrl) {
        when (char) {
            in 'a'..'z', in 'A'..'Z', in '0'..'9', '-', '_', '.', '~' -> append(char)
            ' ' -> append('+')
            else -> {
                val bytes = char.toString().encodeToByteArray()
                for (byte in bytes) {
                    append('%')
                    append(byte.toInt().and(0xFF).toString(16).uppercase().padStart(2, '0'))
                }
            }
        }
    }
}
